import type { DiscoveryProgressResponse, DiscoveryRecords } from './types.ts';
import { authorizedDomainScope } from '../authorization/scope.ts';
import { unitTaskLabel } from './activityLog.ts';
import {
  coverageForEvidence,
  findingTaskLane,
  findingId,
  findingTitle,
  isProgressFinding,
  isSecurityFinding,
  formatEvidenceLine,
  formatSourceLabel,
  parseEvidenceDns,
  parseNetworkEvidence,
} from './evidenceSummary.ts';
import styles from './DnsSummaryCard.module.css';

export interface DnsSummaryCardProps {
  records?: DiscoveryRecords | null;
  progress?: DiscoveryProgressResponse | null;
  isActive?: boolean;
  domain?: string;
}

interface ParsedRecord {
  id: string;
  name: string;
  type: string;
  value: string;
  status: string;
  capturedAt?: string;
  source?: string;
}

function evidenceClock(iso?: string): string {
  if (!iso) return '';
  return iso.slice(11, 19) || iso;
}

function policyState(findingSeverity: string | undefined, hasRecord: boolean) {
  if (findingSeverity === 'CRITICAL' || findingSeverity === 'HIGH') {
    return `Critical · ${findingSeverity}`;
  }
  if (findingSeverity) return `Warning · ${findingSeverity}`;
  if (hasRecord) return 'Enforced';
  return 'Not observed';
}

export function DnsSummaryCard({
  records,
  progress,
  isActive = false,
  domain: propDomain,
}: DnsSummaryCardProps) {
  const attempt = records?.attempt;
  const coverage = attempt?.coverage ?? [];
  const evidence = records?.evidence ?? [];
  const progressFindingById = new Map(
    (progress?.findings ?? [])
      .filter(isProgressFinding)
      .map((finding) => [findingId(finding), finding]),
  );
  const recordFindings = (records?.findings ?? []).map((finding) => {
    const projected = progressFindingById.get(findingId(finding));
    if (!projected) return finding;
    return {
      ...finding,
      lane: finding.lane ?? projected.lane,
      normalizedSeverity: finding.normalizedSeverity ?? projected.normalizedSeverity,
    };
  });
  const seenFindingIds = new Set(recordFindings.map((finding) => findingId(finding)));
  const findings = [
    ...recordFindings,
    ...(progress?.findings ?? [])
      .filter(isProgressFinding)
      .filter((finding) => !seenFindingIds.has(findingId(finding)))
      .map((finding) => ({
        findingId: findingId(finding),
        title: findingTitle(finding),
        normalizedSeverity: finding.normalizedSeverity,
        lane: finding.lane,
      })),
  ];
  const domain =
    propDomain || attempt?.authority?.scopeDomains?.find((scope) => !scope.startsWith('*.')) || '';

  const sourceRecords = records?.sourceRecords ?? [];
  const dnsRecords: ParsedRecord[] = evidence
    .filter((item) => item.kind === 'DNS_RECORD')
    .map((item) => {
      const parsed = parseEvidenceDns(item.summary);
      const parent = coverageForEvidence(coverage, sourceRecords, item.sourceRecordId);
      return {
        id: item.evidenceId,
        name: parent?.target ?? domain,
        type: parsed.type,
        value: parsed.value,
        status: 'Observed',
        capturedAt: item.capturedAt,
        source: formatSourceLabel(sourceRecords, item.sourceRecordId),
      };
    });

  const displayRecords: ParsedRecord[] =
    dnsRecords.length > 0
      ? dnsRecords
      : coverage
          .filter((unit) => unit.method === 'DNS_QUERY')
          .map((unit) => ({
            id: unit.coverageUnitId,
            name: unit.target,
            type: unitTaskLabel(unit),
            value: `${unit.observationCount} record${unit.observationCount === 1 ? '' : 's'} observed${
              unit.limitation ? ` · ${unit.limitation}` : ''
            }`,
            status: unit.status === 'COMPLETE' ? 'Complete' : unit.status,
            capturedAt: unit.finishedAt ?? unit.startedAt,
          }));

  const networkRecords: ParsedRecord[] = evidence
    .filter(
      (item) => item.kind === 'NETWORK_ENDPOINT' && !item.summary?.startsWith('CANDIDATE '),
    )
    .map((item) => {
      const parsed = parseNetworkEvidence(item.summary);
      const parent = coverageForEvidence(coverage, sourceRecords, item.sourceRecordId);
      return {
        id: item.evidenceId,
        name: parent?.target ?? domain,
        type: parsed.type,
        value: parsed.value,
        status: 'Observed',
        capturedAt: item.capturedAt,
        source: formatSourceLabel(sourceRecords, item.sourceRecordId),
      };
    });

  const displayNetwork: ParsedRecord[] =
    networkRecords.length > 0
      ? networkRecords
      : coverage
          .filter((unit) => unit.method === 'NETWORK_PROBE')
          .map((unit) => ({
            id: unit.coverageUnitId,
            name: unit.target,
            type: unitTaskLabel(unit),
            value: `${unit.observationCount} observation${unit.observationCount === 1 ? '' : 's'}${
              unit.limitation ? ` · ${unit.limitation}` : ''
            }`,
            status: unit.status === 'COMPLETE' ? 'Complete' : unit.status,
            capturedAt: unit.finishedAt ?? unit.startedAt,
          }));

  // Attack surface: subdomains discovered and IP addresses resolved for the scope domain.
  const isScopeSubdomain = (host: string) =>
    Boolean(host) && Boolean(domain) && host !== domain && host.endsWith(`.${domain}`);
  const subdomainSet = new Set<string>();
  evidence.forEach((item) => {
    if (item.kind !== 'NETWORK_ENDPOINT') return;
    const summary = item.summary ?? '';
    const host = summary.startsWith('CT ')
      ? summary.slice(3).trim()
      : summary.startsWith('SUBDOMAIN ')
        ? summary.slice(10).trim()
        : '';
    if (isScopeSubdomain(host)) subdomainSet.add(host);
  });
  [...dnsRecords, ...networkRecords].forEach((record) => {
    if (isScopeSubdomain(record.name)) subdomainSet.add(record.name);
  });
  const subdomains = [...subdomainSet].sort();
  const ipSeen = new Set<string>();
  const ipAddresses = dnsRecords
    .filter((record) => record.type === 'A' || record.type === 'AAAA')
    .filter((record) => {
      if (ipSeen.has(record.value)) return false;
      ipSeen.add(record.value);
      return true;
    })
    .map((record) => ({
      id: record.id,
      ip: record.value,
      host: record.name,
      version: record.type === 'AAAA' ? 'IPv6' : 'IPv4',
    }));
  const hasSurface = subdomains.length > 0 || ipAddresses.length > 0;

  const spfFinding = findings.find(
    (finding) =>
      finding.title.toLowerCase().includes('spf') ||
      ('description' in finding && finding.description?.toLowerCase().includes('spf')),
  );
  const dmarcFinding = findings.find(
    (finding) =>
      finding.title.toLowerCase().includes('dmarc') ||
      ('description' in finding && finding.description?.toLowerCase().includes('dmarc')),
  );
  const hasSpfRecord = evidence.some((item) => item.summary?.toLowerCase().includes('v=spf1'));
  const hasDmarcRecord = evidence.some((item) => item.summary?.toLowerCase().includes('v=dmarc1'));
  const hasPolicySignal = Boolean(spfFinding || dmarcFinding || hasSpfRecord || hasDmarcRecord);
  const hasNetworkScope =
    coverage.some((unit) => unit.method === 'NETWORK_PROBE') ||
    Boolean(progress?.units.some((unit) => unit.method === 'NETWORK_PROBE')) ||
    evidence.some((item) => item.kind === 'NETWORK_ENDPOINT');
  const errorRecords = evidence.filter((item) => item.kind === 'ERROR_RECORD');

  const securityFindings = findings.filter(isSecurityFinding);
  const dnsFindings = securityFindings.filter(
    (finding) => findingTaskLane(finding, evidence) === 'DNS',
  );
  const networkFindings = securityFindings.filter(
    (finding) => findingTaskLane(finding, evidence) === 'Network',
  );
  const mixedFindings = securityFindings.filter(
    (finding) => findingTaskLane(finding, evidence) === 'Mixed',
  );

  return (
    <section
      className={styles.dnsCard}
      aria-label="DNS and network task findings"
      data-testid="dns-summary-card"
    >
      {attempt?.authority?.scopeDomains?.length ? (
        <p className={styles.emptyStateText}>
          <strong>Authorized domain scope:</strong>{' '}
          {authorizedDomainScope(attempt.authority.scopeDomains)}
        </p>
      ) : null}

      <div className={styles.headerRow}>
        <h3 className={styles.sectionTitle}>Subdomains and IP addresses</h3>
        {hasSurface && (
          <span className={styles.count}>
            {subdomains.length} {subdomains.length === 1 ? 'subdomain' : 'subdomains'} ·{' '}
            {ipAddresses.length} {ipAddresses.length === 1 ? 'address' : 'addresses'}
          </span>
        )}
      </div>
      {ipAddresses.length > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.dnsTable} aria-label="Resolved IP addresses">
            <thead>
              <tr>
                <th scope="col">IP address</th>
                <th scope="col">Version</th>
                <th scope="col">Host</th>
              </tr>
            </thead>
            <tbody>
              {ipAddresses.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <span className={styles.recordValue}>{entry.ip}</span>
                  </td>
                  <td className={styles.recordType}>{entry.version}</td>
                  <td>{entry.host}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.emptyStateText}>
          {isActive ? 'Resolving IP addresses…' : 'No IP addresses resolved in the latest attempt.'}
        </p>
      )}
      {subdomains.length > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.dnsTable} aria-label="Discovered subdomains">
            <thead>
              <tr>
                <th scope="col">Subdomain</th>
              </tr>
            </thead>
            <tbody>
              {subdomains.map((host) => (
                <tr key={host}>
                  <td>
                    <span className={styles.recordValue}>{host}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.emptyStateText}>
          {isActive ? 'Enumerating subdomains…' : 'No subdomains discovered in the latest attempt.'}
        </p>
      )}

      <h3 className={styles.sectionTitle}>Mail policy and authentication</h3>
      {hasPolicySignal ? (
        <div className={styles.tableWrapper}>
          <table className={styles.dnsTable} aria-label="Mail policy">
            <thead>
              <tr>
                <th scope="col">Control</th>
                <th scope="col">State</th>
                <th scope="col">Recorded observation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">SPF (RFC 7208)</th>
                <td>{policyState(spfFinding?.normalizedSeverity, hasSpfRecord)}</td>
                <td>
                  {spfFinding?.title ??
                    (hasSpfRecord ? 'SPF policy observed.' : 'No SPF policy record observed.')}
                </td>
              </tr>
              <tr>
                <th scope="row">DMARC (RFC 7489)</th>
                <td>{policyState(dmarcFinding?.normalizedSeverity, hasDmarcRecord)}</td>
                <td>
                  {dmarcFinding?.title ??
                    (hasDmarcRecord
                      ? 'DMARC policy observed.'
                      : 'No DMARC policy record observed.')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.emptyStateText}>
          No mail-policy records have been observed
          {domain ? ` for ${domain}` : ''}.
        </p>
      )}

      <div className={styles.headerRow}>
        <h3 className={styles.sectionTitle}>Observed DNS records</h3>
        {displayRecords.length > 0 && (
          <span className={styles.count}>
            {displayRecords.length} {displayRecords.length === 1 ? 'record' : 'records'}
          </span>
        )}
      </div>

      {displayRecords.length > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.dnsTable} aria-label="Observed DNS records">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Name</th>
                <th scope="col">Value</th>
                <th scope="col">Time</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {displayRecords.map((record) => (
                <tr key={record.id}>
                  <td className={styles.recordType}>{record.type}</td>
                  <td>{record.name}</td>
                  <td>
                    <span className={styles.recordValue}>{record.value}</span>
                  </td>
                  <td>
                    {record.capturedAt ? (
                      <time dateTime={record.capturedAt}>{evidenceClock(record.capturedAt)}</time>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{record.source || '—'}</td>
                  <td>{record.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.emptyStateText}>
          {isActive ? 'Resolving DNS records…' : 'No DNS records captured in the latest attempt.'}
        </p>
      )}

      {(displayNetwork.length > 0 || (isActive && hasNetworkScope)) && (
        <>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Observed network endpoints</h3>
            {displayNetwork.length > 0 && (
              <span className={styles.count}>
                {displayNetwork.length} {displayNetwork.length === 1 ? 'endpoint' : 'endpoints'}
              </span>
            )}
          </div>
          {displayNetwork.length > 0 ? (
            <div className={styles.tableWrapper}>
              <table className={styles.dnsTable} aria-label="Observed network endpoints">
                <thead>
                  <tr>
                    <th scope="col">Type</th>
                    <th scope="col">Target</th>
                    <th scope="col">Observation</th>
                    <th scope="col">Time</th>
                    <th scope="col">Source</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayNetwork.map((record) => (
                    <tr key={record.id}>
                      <td className={styles.recordType}>{record.type}</td>
                      <td>{record.name}</td>
                      <td>
                        <span className={styles.recordValue}>{record.value}</span>
                      </td>
                      <td>
                        {record.capturedAt ? (
                          <time dateTime={record.capturedAt}>
                            {evidenceClock(record.capturedAt)}
                          </time>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{record.source || '—'}</td>
                      <td>{record.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.emptyStateText}>
              {isActive
                ? 'Waiting for network observations…'
                : 'No network endpoints captured in the latest attempt.'}
            </p>
          )}
        </>
      )}

      {errorRecords.length > 0 && (
        <>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Failed lookups</h3>
            <span className={styles.count}>
              {errorRecords.length} {errorRecords.length === 1 ? 'error' : 'errors'}
            </span>
          </div>
          <ul className={styles.findingList}>
            {errorRecords.map((item) => {
              const parent = coverageForEvidence(coverage, sourceRecords, item.sourceRecordId);
              const source = formatSourceLabel(sourceRecords, item.sourceRecordId);
              const network =
                parent?.method === 'NETWORK_PROBE' || parent?.targetKind === 'IP_ADDRESS';
              return (
                <li key={item.evidenceId}>
                  <span className={styles.findingTitle}>
                    {formatEvidenceLine(item.kind, item.summary)}
                  </span>
                  <span className={styles.findingMeta}>
                    {network ? 'Network' : 'DNS'} · {parent?.target ?? 'lookup'} failed
                    {source ? ` · ${source}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {(dnsFindings.length > 0 || networkFindings.length > 0 || mixedFindings.length > 0) && (
        <>
          <h3 className={styles.sectionTitle}>Task findings</h3>
          <ul className={styles.findingList}>
            {dnsFindings.map((finding) => (
              <li key={finding.findingId}>
                <span className={styles.findingTitle}>{findingTitle(finding)}</span>
                <span className={styles.findingMeta}>DNS · {finding.normalizedSeverity}</span>
              </li>
            ))}
            {networkFindings.map((finding) => (
              <li key={finding.findingId}>
                <span className={styles.findingTitle}>{findingTitle(finding)}</span>
                <span className={styles.findingMeta}>Network · {finding.normalizedSeverity}</span>
              </li>
            ))}
            {mixedFindings.map((finding) => (
              <li key={finding.findingId}>
                <span className={styles.findingTitle}>{findingTitle(finding)}</span>
                <span className={styles.findingMeta}>Mixed · {finding.normalizedSeverity}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
